import { useMemo, useRef, useState } from 'react';
import { ChevronDown, Columns3, FileText, Plus, Trash2, UploadCloud, X } from 'lucide-react';
import { Button, Chip, Modal } from '@/ui/primitives';
import { formatName } from '@/lib/format';
import { ApiError } from '@/lib/api-client';
import { useDeleteDocumento, useDocumentos, useUploadDocumento } from '../api';
import type { ClienteDetail, Documento } from '../schemas';
import { DocumentoImovelHybridModal } from './DocumentoImovelHybridModal';

interface Props {
  cliente: ClienteDetail;
}

interface ImovelMeta {
  denominacao: string | null;
  matricula: string | null;
  municipio: string | null;
  uf: string | null;
}

interface HybridSelection {
  imovel: Record<string, unknown>;
  docs: Documento[];
  initialDocId?: string | number | null;
}

const TIPO_LABEL: Record<string, string> = {
  CCIR: 'CCIR',
  ITR: 'ITR',
  ITR_declaracao: 'ITR - Declaração',
  ITR_recibo: 'ITR - Recibo',
  certidao_inteiro_teor: 'Certidão de Inteiro Teor',
  CAR: 'CAR',
  dap_caf: 'DAP/CAF',
  memorial_descritivo: 'Memorial Descritivo',
  arrendamento: 'Contrato de Arrendamento',
  licenca_ambiental: 'Licença Ambiental',
  proposta_credito: 'Proposta de Crédito',
  rg: 'RG',
  certidao_casamento: 'Certidão de Casamento',
  contrato: 'Contrato',
  outros: 'Outros',
};

const TIPO_GROUPS: Array<{ label: string; options: Array<[string, string]> }> = [
  {
    label: 'Fundiário',
    options: [
      ['CCIR', 'CCIR'],
      ['ITR_declaracao', 'ITR - Declaração'],
      ['ITR_recibo', 'ITR - Recibo'],
      ['certidao_inteiro_teor', 'Certidão de Inteiro Teor'],
      ['memorial_descritivo', 'Memorial Descritivo'],
      ['arrendamento', 'Contrato de Arrendamento'],
    ],
  },
  {
    label: 'Ambiental',
    options: [
      ['CAR', 'CAR'],
      ['licenca_ambiental', 'Licença Ambiental'],
    ],
  },
  {
    label: 'Crédito',
    options: [
      ['dap_caf', 'DAP/CAF'],
      ['proposta_credito', 'Proposta de Crédito'],
    ],
  },
  {
    label: 'Pessoal',
    options: [
      ['rg', 'RG'],
      ['certidao_casamento', 'Certidão de Casamento'],
    ],
  },
];

export function ClienteDocumentosTab({ cliente }: Props) {
  const { data: docs = [], isLoading } = useDocumentos(cliente.cpf);
  const del = useDeleteDocumento(cliente.cpf);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | number | null>(null);
  const [hybridSelection, setHybridSelection] = useState<HybridSelection | null>(null);

  function handleDelete(docId: string | number | null | undefined) {
    if (docId == null) return;
    if (!confirm('Excluir este documento? Esta ação não pode ser desfeita.')) return;
    del.mutate(docId);
  }

  // Mapa id → metadata do imóvel (denominação, matrícula, município, UF).
  // Lê cliente.imoveis[] (próprios) E cliente.arrendamentos[].imovel (terceiros).
  const imovelById = useMemo(() => {
    const m = new Map<string, ImovelMeta & { raw: Record<string, unknown> }>();
    for (const im of cliente.imoveis ?? []) {
      if (im.id != null) {
        m.set(String(im.id), {
          denominacao: im.denominacao ?? null,
          matricula: im.matricula ?? null,
          municipio: im.municipio ?? null,
          uf: im.uf ?? null,
          raw: im as Record<string, unknown>,
        });
      }
    }
    // Imóveis de arrendamento (terceiros) — vêm aninhados em arrendamentos[].imovel
    const arr = (cliente as { arrendamentos?: Array<{ imovel?: Record<string, unknown> | null }> })
      .arrendamentos;
    if (Array.isArray(arr)) {
      for (const a of arr) {
        const im = a.imovel;
        if (!im || im.id == null) continue;
        const id = String(im.id);
        if (m.has(id)) continue; // não sobrescreve se já vier de cliente.imoveis
        m.set(id, {
          denominacao: (im.denominacao as string | null) ?? null,
          matricula: (im.matricula as string | null) ?? null,
          municipio: (im.municipio as string | null) ?? null,
          uf: (im.uf as string | null) ?? null,
          raw: im,
        });
      }
    }
    return m;
  }, [cliente]);

  // Agrupa docs: 1 seção por matrícula (não por imóvel — mesma matrícula com
  // múltiplos contratos viram a mesma propriedade física, merged numa seção
  // só pra evitar duplicidade visual). Imóveis sem matrícula caem por imovel_id
  // individual. "Documentos gerais" pros sem vínculo.
  const groups = useMemo(() => {
    type Group = {
      id: string;
      label: string;
      meta: ImovelMeta | null;
      imovel: Record<string, unknown> | null;
      docs: Documento[];
    };
    const byMatricula = new Map<string, Group>();
    const semMatricula: Group[] = [];
    const semImovel: Documento[] = [];

    for (const d of docs) {
      if (d.imovel_id == null || d.imovel_id === '') {
        semImovel.push(d);
        continue;
      }
      const imovelId = String(d.imovel_id);
      const meta = imovelById.get(imovelId) ?? {
        denominacao: d.imovel_denominacao ?? null,
        matricula: d.imovel_matricula ?? null,
        municipio: d.imovel_municipio ?? null,
        uf: d.imovel_uf ?? null,
        raw: {
          id: imovelId,
          denominacao: d.imovel_denominacao,
          matricula: d.imovel_matricula,
          municipio: d.imovel_municipio,
          uf: d.imovel_uf,
        },
      };
      // Merge key: matrícula quando disponível; senão imóvel id (mantém grupos
      // separados pra imóveis sem matrícula cadastrada).
      const mergeKey = meta?.matricula ? `mat:${meta.matricula}` : `im:${imovelId}`;
      let group: Group;
      if (meta?.matricula) {
        group = byMatricula.get(mergeKey) ?? {
          id: imovelId,
          label: meta.denominacao || `Imóvel #${imovelId}`,
          meta,
          imovel: meta.raw,
          docs: [],
        };
        if (!byMatricula.has(mergeKey)) byMatricula.set(mergeKey, group);
      } else {
        let existing = semMatricula.find((g) => g.id === imovelId);
        if (!existing) {
          existing = {
            id: imovelId,
            label: meta?.denominacao || `Imóvel #${imovelId}`,
            meta,
            imovel: meta.raw,
            docs: [],
          };
          semMatricula.push(existing);
        }
        group = existing;
      }
      group.docs.push(d);
    }

    const list: Group[] = [...byMatricula.values(), ...semMatricula];
    list.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    if (semImovel.length) {
      list.push({
        id: '__sem__',
        label: 'Documentos gerais do cliente',
        meta: null,
        imovel: null,
        docs: semImovel,
      });
    }
    return list;
  }, [docs, imovelById]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text">Documentos do cliente</h3>
        <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>
          <Plus size={14} strokeWidth={1.5} />
          Anexar
        </Button>
      </div>

      {isLoading ? (
        <p className="text-xs text-text-muted py-4">Carregando documentos…</p>
      ) : docs.length === 0 ? (
        <p className="text-xs text-text-muted py-4">Nenhum documento cadastrado.</p>
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map((group) => {
            const municipioUf = group.meta?.municipio
              ? `${formatName(group.meta.municipio)}${group.meta.uf ? `/${group.meta.uf}` : ''}`
              : null;
            return (
              <details key={group.id} className="group rounded-lg border border-border px-3">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3 [&::-webkit-details-marker]:hidden">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <h4 className="text-[0.82rem] font-semibold text-text truncate">
                      {group.label}
                    </h4>
                    {group.meta?.matricula && (
                      <Chip tone="accent" size="sm">
                        Matrícula {group.meta.matricula}
                      </Chip>
                    )}
                    {municipioUf && (
                      <Chip tone="info" size="sm">
                        {municipioUf}
                      </Chip>
                    )}
                  </div>
                  <span className="flex shrink-0 items-center gap-2 text-[0.7rem] text-text-muted">
                    {group.docs.length} {group.docs.length === 1 ? 'doc' : 'docs'}
                    <ChevronDown
                      size={15}
                      strokeWidth={1.8}
                      className="transition-transform group-open:rotate-180"
                      aria-hidden="true"
                    />
                  </span>
                </summary>
                <ul className="flex flex-col gap-2 border-t border-border py-3">
                  {group.imovel && (
                    <li className="flex justify-end">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          setHybridSelection({ imovel: group.imovel!, docs: group.docs })
                        }
                      >
                        <Columns3 size={14} strokeWidth={1.6} />
                        Visão híbrida do imóvel
                      </Button>
                    </li>
                  )}
                  {group.docs.map((d, i) => (
                    <DocumentoCard
                      key={`${d.id ?? i}`}
                      doc={d}
                      onView={(id) => setPreviewDocId(id)}
                      onHybrid={
                        group.imovel
                          ? (id) =>
                              setHybridSelection({
                                imovel: group.imovel!,
                                docs: group.docs,
                                initialDocId: id,
                              })
                          : undefined
                      }
                      onDelete={() => handleDelete(d.id)}
                    />
                  ))}
                </ul>
              </details>
            );
          })}
        </div>
      )}

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} cliente={cliente} />

      {previewDocId != null && (
        <DocumentoPreview docId={previewDocId} onClose={() => setPreviewDocId(null)} />
      )}

      {hybridSelection && (
        <DocumentoImovelHybridModal
          cpf={cliente.cpf}
          imovel={hybridSelection.imovel}
          docs={hybridSelection.docs}
          initialDocId={hybridSelection.initialDocId}
          onClose={() => setHybridSelection(null)}
        />
      )}
    </div>
  );
}

function DocumentoCard({
  doc,
  onView,
  onHybrid,
  onDelete,
}: {
  doc: Documento;
  onView: (id: string | number) => void;
  onHybrid?: ((id: string | number) => void) | undefined;
  onDelete: () => void;
}) {
  const tipoLabel = TIPO_LABEL[doc.tipo ?? ''] ?? doc.tipo ?? '—';
  const validade = doc.validade ? buildValidade(doc.validade) : null;
  const hasArquivo = !!(doc.arquivo && String(doc.arquivo).trim());
  return (
    <li className="flex items-center gap-3 border border-border rounded-lg p-3 bg-transparent">
      <FileText
        size={20}
        strokeWidth={1.5}
        className={`shrink-0 ${hasArquivo ? 'text-accent' : 'text-text-muted'}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-text">{tipoLabel}</div>
        {!hasArquivo && (
          <div className="text-[0.72rem] text-text-muted mt-0.5">sem arquivo anexado</div>
        )}
        {validade && <div className="mt-1">{validade}</div>}
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
        {doc.id != null && hasArquivo && (
          <>
            {onHybrid && (
              <Button variant="secondary" size="sm" onClick={() => onHybrid(doc.id!)}>
                <Columns3 size={13} strokeWidth={1.5} />
                Analisar
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={() => onView(doc.id!)}>
              Ver
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-danger hover:text-danger"
        >
          <Trash2 size={13} strokeWidth={1.5} />
          Excluir
        </Button>
      </div>
    </li>
  );
}

function buildValidade(validade: string) {
  const vDate = new Date(validade);
  const days = Math.ceil((vDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  const vStr = vDate.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  if (days < 0) {
    return (
      <span className="text-[0.7rem] font-bold px-2 py-0.5 rounded-full bg-danger/10 text-danger">
        Vencido {vStr}
      </span>
    );
  }
  if (days <= 30) {
    return (
      <span className="text-[0.7rem] font-bold px-2 py-0.5 rounded-full bg-warning/10 text-warning">
        Vence em {days}d
      </span>
    );
  }
  return (
    <span className="text-[0.7rem] font-bold px-2 py-0.5 rounded-full bg-success/10 text-success">
      Válido até {vStr}
    </span>
  );
}

function UploadModal({
  open,
  onClose,
  cliente,
}: {
  open: boolean;
  onClose: () => void;
  cliente: ClienteDetail;
}) {
  const upload = useUploadDocumento(cliente.cpf);
  const [files, setFiles] = useState<File[]>([]);
  const [tipo, setTipo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [validade, setValidade] = useState('');
  const [imovelId, setImovelId] = useState('');
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function reset() {
    setFiles([]);
    setTipo('');
    setDescricao('');
    setValidade('');
    setImovelId('');
    setProgress(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleClose() {
    reset();
    onClose();
  }

  function onPickFiles(list: FileList | null) {
    if (!list || !list.length) return;
    setFiles(Array.from(list));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!files.length) {
      setError('Selecione ao menos um arquivo');
      return;
    }
    if (!tipo) {
      setError('Selecione o tipo do documento');
      return;
    }
    setProgress({ current: 0, total: files.length });
    try {
      for (let i = 0; i < files.length; i++) {
        setProgress({ current: i + 1, total: files.length });
        await upload.mutateAsync({
          file: files[i]!,
          tipo,
          ...(descricao && { descricao }),
          ...(validade && { validade }),
          ...(imovelId && { imovelId }),
        });
      }
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Erro no upload');
      setProgress(null);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && handleClose()}
      title="Anexar Documento"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={progress != null}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit as unknown as () => void}
            disabled={progress != null || !files.length || !tipo}
          >
            {progress
              ? files.length > 1
                ? `Enviando ${progress.current}/${progress.total}…`
                : 'Enviando…'
              : 'Enviar'}
          </Button>
        </>
      }
    >
      <form id="upload-doc-form" onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-text-muted block mb-1.5">
            Arquivo *
          </label>
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              onPickFiles(e.dataTransfer.files);
            }}
            className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer bg-surface2 hover:border-accent transition-colors"
          >
            <UploadCloud size={28} strokeWidth={1.5} className="mx-auto mb-2 text-text-muted" />
            <div className="text-sm text-text-2">
              <strong className="text-accent">Clique para selecionar</strong> ou arraste aqui
            </div>
            <div className="text-[0.7rem] text-text-muted mt-1">
              PDF, JPG, PNG, DOCX — múltiplos arquivos aceitos
            </div>
            {files.length > 0 && (
              <div className="mt-3 text-xs text-success font-semibold flex items-center justify-center gap-1.5 flex-wrap">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1">
                    ✓ {f.name}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFiles(files.filter((_, j) => j !== i));
                      }}
                      className="text-text-muted hover:text-danger"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.docx"
            multiple
            className="hidden"
            onChange={(e) => onPickFiles(e.target.files)}
          />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-text-muted block mb-1.5">
            Tipo *
          </label>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            required
            className="h-10 w-full rounded-md bg-elevated border border-border px-2.5 text-sm text-text cursor-pointer transition-colors hover:border-border2 focus:outline-none focus:border-[var(--accent)]"
          >
            <option value="">Selecione…</option>
            {TIPO_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </optgroup>
            ))}
            <option value="outros">Outros</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-text-muted block mb-1.5">
            Descrição
          </label>
          <input
            type="text"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Ex: CCIR 2024/2025"
            className="h-10 w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 text-sm text-text placeholder:text-text-muted shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors focus:outline-none focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_rgba(233,106,69,0.15),0_1px_3px_rgba(0,0,0,0.05)]"
          />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-text-muted block mb-1.5">
            Validade
          </label>
          <input
            type="date"
            value={validade}
            onChange={(e) => setValidade(e.target.value)}
            className="h-10 w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 text-sm text-text placeholder:text-text-muted shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors focus:outline-none focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_rgba(233,106,69,0.15),0_1px_3px_rgba(0,0,0,0.05)]"
          />
        </div>

        {cliente.imoveis.length > 0 && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-text-muted block mb-1.5">
              Imóvel (opcional)
            </label>
            <select
              value={imovelId}
              onChange={(e) => setImovelId(e.target.value)}
              className="h-10 w-full rounded-md bg-elevated border border-border px-2.5 text-sm text-text cursor-pointer transition-colors hover:border-border2 focus:outline-none focus:border-[var(--accent)]"
            >
              <option value="">Nenhum</option>
              {cliente.imoveis.map((im) => (
                <option key={im.id} value={im.id}>
                  {im.denominacao || `Imóvel #${im.id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </form>
    </Modal>
  );
}

function DocumentoPreview({ docId, onClose }: { docId: string | number; onClose: () => void }) {
  const url = `/api/v2/documentos/${docId}/arquivo`;
  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title="Visualizar Documento"
      size="3xl"
      headerActions={
        <>
          <Button variant="ghost" asChild>
            <a href={url} target="_blank" rel="noreferrer">
              Abrir em nova aba
            </a>
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </>
      }
    >
      <iframe src={url} className="w-full h-[70vh] border-0 rounded" title="documento" />
    </Modal>
  );
}
